/** Session key: finance record id to open as refinance wizard. */
import { resolveRecordPhoto } from './financePhotoCache';

export const REFINANCE_PREFILL_KEY = 'finance_refinance_prefill_v1';

export function queueRefinance(recordId: string) {
  try {
    sessionStorage.setItem(REFINANCE_PREFILL_KEY, recordId);
  } catch {
    /* ignore */
  }
}

export function consumeRefinanceQueue(): string | null {
  try {
    const id = sessionStorage.getItem(REFINANCE_PREFILL_KEY);
    if (id) sessionStorage.removeItem(REFINANCE_PREFILL_KEY);
    return id;
  } catch {
    return null;
  }
}

const FINANCIAL_RESET_KEYS = [
  'amount',
  'totalAmount',
  'outPayment',
  'opInterest',
  'amountBalance',
  'interestPercentage',
  'totalInterest',
  'emiPeriod',
  'checkEmiAmount',
  'emiAmount',
  'agreement',
  'agreementFinance',
  'bankTransaction',
  'bank',
  'chqNoDescription',
] as const;

const LIFECYCLE_RESET_KEYS = ['closed', 'closedDate', 'seized', 'seizedDate', 'seizedReason'] as const;

/** Copy a closed finance record into a new application; financial fields cleared for admin entry. */
export function refinanceFormFromClosed(data: Record<string, unknown>): Record<string, unknown> {
  const next = { ...data };
  for (const k of FINANCIAL_RESET_KEYS) next[k] = '';
  for (const k of LIFECYCLE_RESET_KEYS) delete next[k];
  next.refinancedFromId = data.refinancedFromId || data.id || '';
  return next;
}

/** Normalize HA for matching: trim, lower-case, drop spaces / hyphens / HA prefix variants. */
export function normalizeHaNumber(haNumber: string): string {
  return String(haNumber ?? '')
    .trim()
    .toLowerCase()
    .replace(/^ha[\s\-_]*/i, '')
    .replace(/[\s\-_]/g, '');
}

function haNumbersMatch(a: string, b: string): boolean {
  const left = normalizeHaNumber(a);
  const right = normalizeHaNumber(b);
  if (!left || !right) return false;
  return left === right || a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Vehicle + customer fields copied by HA lookup (not facility / EMI). */
export const HA_PREFILL_FIELD_KEYS = [
  'regNo',
  'makers',
  'model',
  'chasisNo',
  'engNo',
  'isCB',
  'isCLR',
  'hpnDate',
  'customerName',
  'mobileNumber',
  'alternateMobile',
  'guarantorName',
  'guarantorMobile',
  'guarantorAddress',
  'refererName',
  'refererNumber',
  'customerAadhar',
  'villageTown',
  'line',
  'customerAddress1',
  'customerAddress2',
  'recordDetails',
  'applicantPhoto',
] as const;

/** Vehicle + customer fields from main ledger (HA lookup). */
export function prefillFromMainLedgerRecord(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of HA_PREFILL_FIELD_KEYS) {
    if (k === 'isCB' || k === 'isCLR') {
      if (data[k] !== undefined) out[k] = data[k];
      continue;
    }
    if (data[k] !== undefined && data[k] !== null && data[k] !== '') out[k] = data[k];
  }
  const photo = resolveRecordPhoto(data);
  if (photo) out.applicantPhoto = photo;
  return out;
}

/** Find a saved finance record by HA number (case-insensitive) in the active workspace. */
export function findFinanceRecordByHa<T extends { id: string; data: Record<string, unknown> }>(
  haNumber: string,
  records: T[],
  excludeId?: string | null,
): T | null {
  const trimmed = haNumber.trim();
  if (!trimmed) return null;
  return (
    records.find((r) => {
      if (excludeId && r.id === excludeId) return false;
      return haNumbersMatch(String(r.data.haNumber ?? ''), trimmed);
    }) ?? null
  );
}
